import type { MapPoint } from "$lib/components/ui/map/_other/mapPoint.type";
import type { Coordinates } from "$lib/features/_other/coordinates.type";
import type { LabelSlug } from "$lib/features/_other/labelSlug";
import type { CityDetails } from "$lib/features/cityDetails/cityDetails.type";
import type { CityFeature, CityFeaturePreview } from "$lib/features/cityFeature/cityFeature.type";
import type { CityFeatureType } from "$lib/features/cityFeature/cityFeatureType.enum";
import type { CountryPreview } from "$lib/features/country/countryPreview.type";
import type { CoverImage } from "$lib/features/image/imageDetails.type";
import type { InsiderTip } from "$lib/features/insiderTip/insiderTip.type";
import type { TimelineEvent } from "$lib/features/timelineEvent/timelineEvent.type";
import { UrlUtil } from "$lib/utils/url.util";

export interface CountryDetails {
    intro: CountryIntro;
    preview: CountryPreview;
    citiesMapPoints: FeatureMapPoint[];
    nearestCountries: CountryPreview[];
}

export interface CountryIntro {
    headline: string;
    body: string;
}

export interface CityMapSummary {
    vibe: string;
    foundedYear: number;
    featureCounts: Record<CityFeatureType, number>;
    featureCoordinates: TypeWithCoordinates[];
}

export interface TypeWithCoordinates {
    type: CityFeatureType;
    coordinates: Coordinates;
}

export interface CityPageData {
    id: string;
    isUniqueName: boolean;
    details: CityDetails;
    feature: CityFeature;
    timelineEvents: TimelineEvent[];
    tips: InsiderTip[];
    activities: CityFeaturePreview[];
    annualFestivals: CityFeaturePreview[];
    architecturalHighlights: CityFeaturePreview[];
    attractions: CityFeaturePreview[];
    cityHighlights: CityFeaturePreview[];
    foods: CityFeaturePreview[];
    freeThingToDos: CityFeaturePreview[];
    greenSpaces: CityFeaturePreview[];
    hiddenGems: CityFeaturePreview[];
    markets: CityFeaturePreview[];
    nightlifeSpots: CityFeaturePreview[];
    photoSpots: CityFeaturePreview[];
    seasonalHighlights: CityFeaturePreview[];
    unescoSites: CityFeaturePreview[];
    viewpoints: CityFeaturePreview[];
    scenicBikeRides: CityFeaturePreview[];
    scenicStrolls: CityFeaturePreview[];
    selfGuidedTours: CityFeaturePreview[];
    itineraries: CityFeaturePreview[];
}

export interface FeatureMapPoint {
    id: string;
    label: string;
    slug: string;
    type: CityFeatureType;
    coords: Coordinates;
    thumbnail: CoverImage | null;
    citySlug: string;
    countrySlug: string;
}

export interface CityFeatureSlugs {
    cityId: string;
    slugsByType: Record<CityFeatureType, string[]>;
}

export interface ChildFeatureLink {
    englishName: string;
    slug: string;
    type: CityFeatureType;
}

export function featureMapPointToMapPoint(item: FeatureMapPoint): MapPoint {
    return {
        id: item.id,
        label: item.label,
        type: item.type,
        coords: item.coords,
        href: UrlUtil.toCityFeature({ countrySlug: item.countrySlug, citySlug: item.citySlug, type: item.type, featureSlug: item.slug }).href,
        imageUrl: item.thumbnail ? UrlUtil.getFullImageUrl(item.thumbnail) : undefined
    };
}

export enum Continent {
    AFRICA = "AFRICA",
    ASIA = "ASIA",
    EUROPE = "EUROPE",
    NORTH_AMERICA = "NORTH_AMERICA",
    SOUTH_AMERICA = "SOUTH_AMERICA",
    OCEANIA = "OCEANIA"
}

export const ALL_CONTINENTS: Continent[] = [
    Continent.AFRICA,
    Continent.ASIA,
    Continent.EUROPE,
    Continent.OCEANIA,
    Continent.NORTH_AMERICA,
    Continent.SOUTH_AMERICA
];

export const CONTINENT_LABELS: Record<Continent, string> = {
    [Continent.AFRICA]: 'Africa',
    [Continent.ASIA]: 'Asia',
    [Continent.EUROPE]: 'Europe',
    [Continent.NORTH_AMERICA]: 'North America',
    [Continent.OCEANIA]: 'Oceania',
    [Continent.SOUTH_AMERICA]: 'South America'
};

export interface CategoryPreviewData {
    type: CityFeatureType;
    count: number;
    representativeImage: CoverImage | null;
}

export interface KeepExploringCategory {
    type: CityFeatureType;
    title: string;
    image: CoverImage | null;
    count: number;
    href: string;
}

export interface LogPayload {
    type: 'ERROR' | 'WARNING' | 'INFO' | 'DEBUG' | 'TRACE';
    message: string;
    stack?: string;
    uri: string;
    url: string;
}

export interface AnalyticsPayload {
    event: string;
    payload: Record<string, any> | undefined;
    uri: string;
    url: string;
    sessionId: string;
    clientCreatedAt: string; // ISO UTC string
}

export interface CollectionItem {
    id: string;
    slug: string;
    type: CityFeatureType;
    englishName: string;
    image: CoverImage;
    coordinates: Coordinates;
    country: LabelSlug;
    city: LabelSlug;
}

export class VelatlasResponseException extends Error implements VelatlasResponseError {
    public title: string;
    public status?: number;
    public detail: string;
    public instance?: string;
    public properties?: Map<string, object>;

    constructor({ title, status, detail, instance, properties }: { title: string, status?: number, detail: string, instance?: string, properties?: Map<string, object> }) {
        super(detail);
        this.title = title;
        this.status = status;
        this.detail = detail;
        this.instance = instance;
        this.properties = properties;
        this.name = "VelatlasResponseException";
    }
}

export interface VelatlasResponseError {
    title: string;
    status?: number;
    detail: string;
    instance?: string;
    properties?: Map<string, object>;
}
